import { makeLogger } from '@/utils/logger/logger.ts';

const AUTH_LOGGER_NAME = 'Authentication';
export const authLogger = makeLogger(AUTH_LOGGER_NAME);

/**
 * Authentication & Timeout Flow Logger
 *
 * Provides structured logging for authentication operations including:
 * - Token refresh operations and failures
 * - Login/logout events
 * - Inactivity timeout checks and actions
 * - Cross-tab activity synchronization
 *
 * Use this logger to filter auth-related logs by searching for '[Authentication]'
 */

// Token Manager logging
export const logTokenRefresh = (reason: 'expired' | 'invalid' | 'pre-request') => {
	authLogger.info(`Token refresh initiated - reason: ${reason}`);
};

export const logTokenRefreshSuccess = (hasNewRefreshToken: boolean) => {
	authLogger.info(`Token refresh successful - new refresh token: ${hasNewRefreshToken}`);
};

export const logTokenRefreshFailure = (error?: string) => {
	authLogger.warn(`Token refresh failed${error ? ` - ${error}` : ''}`);
};

export const logTokenClear = (reason: 'logout' | 'refresh_failure' | 'fatal_error') => {
	authLogger.info(`Tokens cleared - reason: ${reason}`);
};

// Authentication Context logging
export const logLogin = (method: 'email' | 'google' | 'finalize' | 'register') => {
	authLogger.info(`User login via ${method}`);
};

export const logLogout = (reason: 'manual' | 'inactivity' | 'token_expired' | 'error') => {
	authLogger.info(`User logout - reason: ${reason}`);
};

export const logTokenSync = (source: 'storage_event' | 'periodic_check') => {
	authLogger.debug(`Token state synced from ${source}`);
};

// Axios interceptor logging
export const logAuthRequest = (url: string, hasToken: boolean) => {
	authLogger.debug(`Request to ${url} - token attached: ${hasToken}`);
};

export const logAuthResponse = (status: number, url: string, willRetry: boolean) => {
	if (status === 401) {
		authLogger.warn(`401 response from ${url} - will retry: ${willRetry}`);
	}
};

export const logFatalAuthError = (code: string, url: string) => {
	authLogger.error(`Fatal auth error: ${code} from ${url} - redirecting to login`);
};

// Inactivity timeout logging
export const logInactivityCheck = (timeSinceActivity: number, threshold: number) => {
	const minutes = Math.round(timeSinceActivity / 1000 / 60);
	const thresholdMinutes = Math.round(threshold / 1000 / 60);
	authLogger.debug(`Inactivity check - ${minutes}m since activity (threshold: ${thresholdMinutes}m)`);
};

export const logInactivityLogout = (timeSinceActivity: number) => {
	const minutes = Math.round(timeSinceActivity / 1000 / 60);
	authLogger.info(`Inactivity logout triggered - ${minutes} minutes since last activity`);
};

export const logActivityDetected = (source: 'local' | 'cross_tab', timestamp: number) => {
	authLogger.debug(`Activity detected from ${source} at ${new Date(timestamp).toLocaleTimeString()}`);
};

export const logInactivityTimerReset = (reason: 'activity' | 'cross_tab' | 'manual') => {
	authLogger.debug(`Inactivity timer reset - reason: ${reason}`);
};

export const logInactivityMonitorStart = () => {
	authLogger.info('Inactivity monitoring started');
};

export const logInactivityMonitorStop = (reason: 'logout' | 'component_unmount') => {
	authLogger.info(`Inactivity monitoring stopped - reason: ${reason}`);
};
